import React from 'react';
import { useParams } from 'react-router-dom';

import { Card, SimpleGrid, Stack } from '@mantine/core';

import HeavyChainCdr3Distributions from './HeavyChainCdr3Distributions';
import IgChainSummary from './IgChainSummary';
import IgExpression from './IgExpression';
import ProductiveChainSummary from './ProductiveChains';
import SomaticHypermutation from './SomaticHypermutation';
import VGeneDistribution from './VGeneDistribution';
import { LoadingContainer } from '../common/LoadingContainer';
import { useGetCells, useGetExperiment, useGetRepertoireData } from '../store/store';

export function RepertoireAnalysisPage() {
  const { id: experimentId } = useParams();
  const { data: experiment } = useGetExperiment({ id: experimentId! });
  const { data: cellData, isLoading: isLoadingCellData } = useGetCells({ id: experimentId! });
  const { data: repertoireData, isLoading: isLoadingRepertoireData } = useGetRepertoireData({ id: experimentId! });

  const typeOfRanAnalysis = React.useMemo(() => {
    if (experiment?.antibodyFile && experiment.cellFile) {
      return 'A';
    }
    return 'B';
  }, [experiment?.antibodyFile, experiment?.cellFile]);

  const isIgProductivityChartDataMissing = React.useMemo(() => {
    const igProductivityData = repertoireData?.igProductiveChainsRawData;
    return !igProductivityData?.productive && !igProductivityData?.unproductive;
  }, [repertoireData?.igProductiveChainsRawData]);

  return (
    <Stack gap="sm" py="md" px="xs">
      <Stack gap="xl">
        <SimpleGrid cols={2}>
          <Card withBorder>
            <Stack gap="xl">
              {isLoadingRepertoireData ? <LoadingContainer height={300} /> : <IgChainSummary igChainRawData={repertoireData?.igChainRawData} />}
            </Stack>
          </Card>
          <Card withBorder>
            {isLoadingRepertoireData ? (
              <LoadingContainer height={300} />
            ) : (
              !isIgProductivityChartDataMissing && <ProductiveChainSummary igProductiveChainsRawData={repertoireData?.igProductiveChainsRawData} />
            )}
          </Card>
        </SimpleGrid>
        <VGeneDistribution repertoireData={repertoireData} isLoading={isLoadingRepertoireData} />
        <IgExpression
          hasIgIsotype={repertoireData?.hasIgIsotype ?? false}
          typeOfRanAnalysis={typeOfRanAnalysis}
          cellData={cellData}
          isLoading={isLoadingCellData}
        />
        {(isLoadingRepertoireData || repertoireData?.shmDistributionData) && (
          <SomaticHypermutation
            shmDistributionData={repertoireData?.shmDistributionData ?? undefined}
            shmViolinData={repertoireData?.shmViolinData ?? undefined}
            isLoading={isLoadingRepertoireData}
          />
        )}
        <HeavyChainCdr3Distributions cdr3LengthDistributionData={repertoireData?.cdr3LengthDistributionData ?? undefined} isLoading={isLoadingRepertoireData} />
      </Stack>
    </Stack>
  );
}
